/**
 * i18next configuration and initialization.
 * This file sets up i18next with custom language detector, HTTP backend,
 * and React integration.
 *
 * The configuration uses:
 * - Custom language detector (user profile → localStorage → browser → fallback)
 * - HTTP backend for lazy loading translation files
 * - React integration via react-i18next
 * - Namespaces: common, wellbeing, auth
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import { customLanguageDetector } from './languageDetector';
import type { Language } from './types';

/**
 * Immutable configuration object for i18next.
 * Defines all settings for language detection, resource loading, and behavior.
 */
const i18nConfig = {
  /**
   * Fallback language when translation is missing.
   * Always defaults to English.
   */
  fallbackLng: 'en' as Language,

  /**
   * Enable debug mode in development for troubleshooting.
   * Logs missing keys and other i18n events to console.
   */
  debug: import.meta.env.DEV,

  /**
   * Namespaces for organizing translations.
   * - common: Shared UI elements (buttons, navigation, errors)
   * - wellbeing: Wellness-specific content (radar, categories, domains)
   * - auth: Authentication-related content (login, logout)
   * - selfcare: Self-care dashboard and related components
   * - consent: Consent screen UI chrome
   * - assist: Assist dashboard and related components
   */
  ns: ['common', 'wellbeing', 'auth', 'selfcare', 'consent', 'assist', 'setup', 'start', 'onboarding'] as const,

  /**
   * Default namespace used when no namespace is specified.
   */
  defaultNS: 'common' as const,

  /**
   * Interpolation settings.
   * escapeValue: false because React already escapes values
   */
  interpolation: {
    escapeValue: false,
  },

  /**
   * React-specific settings.
   * - useSuspense: true to enable React Suspense for loading states
   */
  react: {
    useSuspense: true,
  },

  /**
   * HTTP backend settings for lazy loading translation files.
   * Files are loaded from /locales/{language}/{namespace}.json
   */
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
  },

  /**
   * Key separator for nested translations.
   * Use dot notation: t('app.name')
   */
  keySeparator: '.',

  /**
   * Namespace separator for explicit namespace usage.
   * Use colon: t('wellbeing:title')
   */
  nsSeparator: ':',
} as const;

/**
 * Handler for missing translation keys.
 * In development, warns about missing keys to aid debugging.
 * In production, silently returns the key to avoid console noise.
 *
 * @param lngs - The languages being used
 * @param ns - The namespace
 * @param key - The missing translation key
 * @param fallbackValue - The fallback value
 */
const handleMissingKey = (
  lngs: readonly string[],
  ns: string,
  key: string,
  _fallbackValue: string,
): void => {
  if (import.meta.env.DEV) {
    const lng = lngs[0] || 'unknown';
    console.warn(`Missing translation: [${lng}] ${ns}:${key}`);
  }
};

/**
 * Initialize i18next with all plugins and configuration.
 * This is a side effect that sets up the global i18n instance.
 *
 * Plugins are applied in order:
 * 1. customLanguageDetector - Detects user's language
 * 2. HttpBackend - Loads translation files from server
 * 3. initReactI18next - Integrates with React
 */
i18next
  .use(customLanguageDetector)
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    ...i18nConfig,
    // Add missing key handler
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: handleMissingKey,
  })
  .catch((error) => {
    console.error('Failed to initialize i18next:', error);
  });

/**
 * Export the configured i18n instance.
 * Use this for programmatic language changes or direct access to i18n API.
 *
 * @example
 * ```typescript
 * import i18n from '@/i18n/config';
 *
 * // Change language
 * await i18n.changeLanguage('nl');
 *
 * // Get current language
 * const currentLang = i18n.language;
 * ```
 */
export default i18next;
